import { ConnectError } from '@connectrpc/connect';
import type { RevitOperationProgressSnapshot } from './operationProgressClient';
import { pollRevitOperation } from './operationProgressClient';
import { useIntegrationDiagnosticsStore } from '../../stores/useIntegrationDiagnosticsStore';
import { getPcfMappingGrpcClient } from './pcfMappingGrpcClient';

type ProgressOptions = {
  readonly onProgress?: (progress: RevitOperationProgressSnapshot) => void;
};

type SimpleResult = {
  readonly success: boolean;
  readonly error?: string;
};

function unavailableResult(operation: string): SimpleResult {
  const error = 'gRPC transport is unavailable';
  useIntegrationDiagnosticsStore.getState().setLastGrpcError(`${operation}: ${error}`);
  return { success: false, error };
}

export async function getMappingsGrpc(): Promise<{
  readonly success: boolean;
  readonly configJson?: string;
  readonly error?: string;
}> {
  const client = getPcfMappingGrpcClient();
  if (!client) return unavailableResult('GetMappings');
  try {
    const response = await client.getMappings({});
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(
      response.isSuccess ? null : `GetMappings: ${response.error}`,
    );
    return {
      success: response.isSuccess,
      ...(response.configJson ? { configJson: response.configJson } : {}),
      ...(response.error ? { error: response.error } : {}),
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'GetMappings gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetMappings: ${message}`);
    return { success: false, error: message };
  }
}

async function runProgressOperation(
  operationName: string,
  operationId: string,
  options: ProgressOptions,
): Promise<SimpleResult> {
  const operationResult = await pollRevitOperation(operationId, {
    onProgress: options.onProgress,
    parseResult: parseSimpleMappingResultJson,
  });
  if (!operationResult.success || !operationResult.result) {
    const error = operationResult.error || `${operationName} operation failed`;
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`${operationName}: ${error}`);
    return { success: false, error };
  }
  return operationResult.result;
}

export async function saveMappingsGrpc(configJson: string, options?: ProgressOptions): Promise<SimpleResult> {
  const client = getPcfMappingGrpcClient();
  if (!client) return unavailableResult('SaveMappings');
  try {
    const request = { configJson };
    if (options?.onProgress) {
      try {
        const start = await client.startSaveMappings(request);
        if (start.isSuccess && start.operationId) {
          return runProgressOperation('SaveMappings', start.operationId, options);
        }
      } catch (error) {
        // Older connector builds only expose the unary method.
        if (!(error instanceof ConnectError)) throw error;
      }
    }
    const response = await client.saveMappings(request);
    return adaptSimpleResponse('SaveMappings', response.isSuccess, response.error);
  } catch (error) {
    return failedResult('SaveMappings', error);
  }
}

export async function resetMappingsToDefaultsGrpc(options?: ProgressOptions): Promise<SimpleResult> {
  const client = getPcfMappingGrpcClient();
  if (!client) return unavailableResult('ResetMappingsToDefaults');
  try {
    if (options?.onProgress) {
      try {
        const start = await client.startResetToDefaults({});
        if (start.isSuccess && start.operationId) {
          return runProgressOperation('ResetMappingsToDefaults', start.operationId, options);
        }
      } catch (error) {
        // Older connector builds only expose the unary method.
        if (!(error instanceof ConnectError)) throw error;
      }
    }
    const response = await client.resetToDefaults({});
    return adaptSimpleResponse('ResetMappingsToDefaults', response.isSuccess, response.error);
  } catch (error) {
    return failedResult('ResetMappingsToDefaults', error);
  }
}

export async function exportMappingsGrpc(): Promise<{
  readonly success: boolean;
  readonly json?: string;
  readonly error?: string;
}> {
  const client = getPcfMappingGrpcClient();
  if (!client) return unavailableResult('ExportMappings');
  try {
    const response = await client.exportMappings({});
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(
      response.isSuccess ? null : `ExportMappings: ${response.error}`,
    );
    return {
      success: response.isSuccess,
      ...(response.json ? { json: response.json } : {}),
      ...(response.error ? { error: response.error } : {}),
    };
  } catch (error) {
    const failed = failedResult('ExportMappings', error);
    return failed;
  }
}

export async function importMappingsGrpc(json: string, options?: ProgressOptions): Promise<SimpleResult> {
  const client = getPcfMappingGrpcClient();
  if (!client) return unavailableResult('ImportMappings');
  try {
    const request = { json };
    if (options?.onProgress) {
      try {
        const start = await client.startImportMappings(request);
        if (start.isSuccess && start.operationId) {
          return runProgressOperation('ImportMappings', start.operationId, options);
        }
      } catch (error) {
        // Older connector builds only expose the unary method.
        if (!(error instanceof ConnectError)) throw error;
      }
    }
    const response = await client.importMappings(request);
    return adaptSimpleResponse('ImportMappings', response.isSuccess, response.error);
  } catch (error) {
    return failedResult('ImportMappings', error);
  }
}

function adaptSimpleResponse(operation: string, success: boolean, error: string): SimpleResult {
  useIntegrationDiagnosticsStore.getState().setLastGrpcError(
    success ? null : `${operation}: ${error}`,
  );
  return { success, ...(error ? { error } : {}) };
}

function failedResult(operation: string, error: unknown): SimpleResult {
  const message = error instanceof Error ? error.message : `${operation} gRPC error`;
  useIntegrationDiagnosticsStore.getState().setLastGrpcError(`${operation}: ${message}`);
  return { success: false, error: message };
}

function parseSimpleMappingResultJson(resultJson: string): SimpleResult {
  const parsed: unknown = JSON.parse(resultJson);
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { success: false, error: 'Invalid mapping operation result' };
  }
  const success = 'isSuccess' in parsed && parsed.isSuccess === true;
  const error = 'error' in parsed && typeof parsed.error === 'string' ? parsed.error : '';
  return { success, ...(error ? { error } : {}) };
}
